<template>
  <div class="all-webinars-page">
    <section class="webinars-hero-section">
      <div class="container">
        <h1 class="webinars-hero-title">All Webinars</h1>
        <p class="webinars-hero-subtitle">Learn from industry experts and franchise leaders</p>
      </div>
    </section>
    <section class="webinars-list-section">
      <div class="container">
        <div v-if="webinarStore.loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2">Loading webinars...</p>
        </div>
        <div v-else-if="webinarStore.error" class="alert alert-warning text-center my-5" role="alert">
          <h4 class="alert-heading">Unable to load webinars</h4>
          <p>{{ webinarStore.error }}</p>
          <button @click="loadWebinars" class="btn btn-primary">Try Again</button>
        </div>
        <div v-else-if="paginatedWebinars.length === 0" class="text-center py-5 dummy-empty-state">
          <div class="dummy-icon">
            <i class="fa fa-video-camera"></i>
          </div>
          <h4>No webinars available yet</h4>
          <p>Check back soon for upcoming webinars!</p>
        </div>
        <div v-else class="webinars-grid">
          <WebinarCard 
            v-for="webinar in paginatedWebinars" 
            :key="webinar.id" 
            :webinar="webinar" 
            class="webinar-card-grid"
          />
        </div>
        <div v-if="totalPages > 1" class="pagination-container">
          <div class="pagination">
            <button 
              @click="currentPage = 1" 
              :disabled="currentPage === 1"
              class="pagination-btn"
            >
              <i class="fa fa-angle-double-left"></i>
            </button>
            <button 
              @click="currentPage--" 
              :disabled="currentPage === 1"
              class="pagination-btn"
            >
              <i class="fa fa-angle-left"></i>
            </button>
            <div class="page-numbers">
              <button 
                v-for="page in visiblePages" 
                :key="page"
                @click="currentPage = page"
                :class="['page-btn', { active: currentPage === page }]"
              >
                {{ page }}
              </button>
            </div>
            <button 
              @click="currentPage++" 
              :disabled="currentPage === totalPages"
              class="pagination-btn"
            >
              <i class="fa fa-angle-right"></i>
            </button>
            <button 
              @click="currentPage = totalPages" 
              :disabled="currentPage === totalPages"
              class="pagination-btn"
            >
              <i class="fa fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useWebinarStore } from '../stores/webinarStore'
import WebinarCard from '../components/WebinarSection.vue'

const webinarStore = useWebinarStore()
const currentPage = ref(1)
const itemsPerPage = ref(9)

const loadWebinars = async () => {
  await webinarStore.fetchWebinars()
}

onMounted(() => {
  if (webinarStore.webinars.length === 0) {
    loadWebinars()
  }
})

const sortedWebinars = computed(() => {
  return [...webinarStore.webinars].sort((a, b) => new Date(b.date) - new Date(a.date))
})

const totalPages = computed(() => Math.ceil(sortedWebinars.value.length / itemsPerPage.value))
const paginatedWebinars = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  return sortedWebinars.value.slice(start, start + itemsPerPage.value)
})
const visiblePages = computed(() => {
  const pages = []
  const maxVisible = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(totalPages.value, start + maxVisible - 1)
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})
</script>

<style scoped>
.all-webinars-page {
  min-height: 100vh;
  background: #f8fafc;
}
.webinars-hero-section {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  padding: 64px 0 40px 0;
  color: #fff;
  text-align: center;
}
.webinars-hero-title {
  font-size: 2.8rem;
  font-weight: 800;
  margin-bottom: 12px;
  letter-spacing: -0.5px;
}
.webinars-hero-subtitle {
  font-size: 1.15rem;
  opacity: 0.95;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}
.webinars-list-section {
  padding: 48px 0 64px 0;
}
.webinars-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 40px;
}
@media (max-width: 1100px) {
  .webinars-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
}
@media (max-width: 700px) {
  .webinars-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}
.webinar-card-grid {
  height: 100%;
  display: flex;
}
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}
.pagination {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pagination-btn {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
}
.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pagination-btn:not(:disabled):hover {
  border-color: #2563eb;
  color: #2563eb;
}
.page-numbers {
  display: flex;
  gap: 4px;
}
.page-btn {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
  min-width: 44px;
}
.page-btn.active {
  background: #2563eb;
  border-color: #2563eb;
  color: white;
}
.page-btn:hover:not(.active) {
  border-color: #2563eb;
  color: #2563eb;
}
.dummy-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #6b7280;
}
.dummy-icon {
  font-size: 4rem;
  color: #d1d5db;
  margin-bottom: 20px;
}
</style> 